import React, { useEffect, useRef, useState } from 'react';
import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import { AlertCircle, Eye } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as Popover from '@radix-ui/react-popover';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/toast';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import { loadMedcall2DiversionParticipantAliases } from './medcallIntakeAliases';
import {
  buildMedcallIntakeSearchVariables,
  MEDCALL_SEARCH_DIVERSION_PARTICIPANTS,
} from './medcallIntakeSearchCompatibility';
import { createMedcallLatestRequestGuard } from './medcallLatestRequestGuard';
import { readMedcall2IntakeOptions, type Medcall2OptionRow } from './medcall2IntakeOptions';
import './Medcall2ParticipantIntakeDialog.css';

const MEDCALL2_PARTICIPANT_INTAKE_OPTIONS = gql`
  query Medcall2ParticipantIntakeOptions {
    medcallOptions: medcall2Options(names: ["programs", "filter_domains", "facilities"]) {
      option_name
      option_value_json
    }
  }
`;

const MEDCALL2_FIND_DUPLICATE_PARTICIPANTS = gql`
  query Medcall2FindDuplicateParticipants(
    $dob: String
    $firstname: String
    $lastname: String
    $odrparticipant: Int
    $participantId: Int
  ) {
    medcallFindDuplicateParticipants(
      dob: $dob
      firstname: $firstname
      lastname: $lastname
      odrparticipant: $odrparticipant
      participantId: $participantId
    ) {
      dob
      firstname
      id
      lastname
      program
    }
  }
`;

const MEDCALL2_CREATE_PARTICIPANT = gql`
  mutation Medcall2CreateParticipant(
    $diversionParticipantId: Int!
    $nurseEmail: String
    $nurseFirstname: String
    $nurseLastname: String
    $nurseTitle: String
    $optionFacilityId: Int
    $preserveLegacyDuplicateSemantics: Boolean
    $programKey: String!
    $psychiatristEmail: String
    $psychiatristFirstname: String
    $psychiatristLastname: String
    $psychiatristTitle: String
  ) {
    medcallCreateParticipant(
      diversionParticipantId: $diversionParticipantId
      nurseEmail: $nurseEmail
      nurseFirstname: $nurseFirstname
      nurseLastname: $nurseLastname
      nurseTitle: $nurseTitle
      optionFacilityId: $optionFacilityId
      preserveLegacyDuplicateSemantics: $preserveLegacyDuplicateSemantics
      programKey: $programKey
      psychiatristEmail: $psychiatristEmail
      psychiatristFirstname: $psychiatristFirstname
      psychiatristLastname: $psychiatristLastname
      psychiatristTitle: $psychiatristTitle
    ) {
      duplicateParticipant {
        dob
        firstname
        id
        lastname
        program
      }
      message
      participant {
        firstname
        id
        lastname
      }
      responseCode
      responseID
    }
  }
`;

const MEDCALL2_ROUTE_BASE = '/tools/medcall2';
const NURSE_TITLES = ['LVN', 'MA', 'CNA', 'RN', 'LPT', 'NP', 'EMT'];
const PSYCHIATRIST_TITLES = ['MD', 'NP', 'PA'];
const EMAIL_ADDRESS_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;

type DiversionParticipantSearchItem = {
  cid?: string | null;
  dob?: string | null;
  firstname?: string | null;
  fullname?: string | null;
  in_use?: boolean | null;
  lastname?: string | null;
  odrparticipant?: number | null;
  participant: number;
  provider?: string | null;
  username?: string | null;
};

type DuplicateParticipant = {
  dob?: string | null;
  firstname?: string | null;
  id: number;
  lastname?: string | null;
  program?: string | null;
};

type Medcall2ParticipantIntakeOptionsData = {
  medcallOptions: Medcall2OptionRow[];
};

type Medcall2SearchDiversionParticipantsData = {
  medcallSearchDiversionParticipants: DiversionParticipantSearchItem[];
};

type Medcall2FindDuplicateParticipantsData = {
  medcallFindDuplicateParticipants: DuplicateParticipant[];
};

type Medcall2CreateParticipantData = {
  medcallCreateParticipant: {
    duplicateParticipant?: DuplicateParticipant | null;
    message?: string | null;
    participant?: {
      firstname?: string | null;
      id: number;
      lastname?: string | null;
    } | null;
    responseCode?: number | null;
    responseID?: number | null;
  };
};

type IntakeFormValues = {
  nurseConfirmEmail: string;
  nurseEmail: string;
  nurseFirstname: string;
  nurseLastname: string;
  nurseTitle: string;
  optionFacilityId: string;
  programKey: string;
  psychiatristConfirmEmail: string;
  psychiatristEmail: string;
  psychiatristFirstname: string;
  psychiatristLastname: string;
  psychiatristTitle: string;
};

export type Medcall2ParticipantIntakeDialogProps = {
  onCreated?: (participantId: number) => Promise<void> | void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
};

const DEFAULT_FORM_VALUES: IntakeFormValues = {
  nurseConfirmEmail: '',
  nurseEmail: '',
  nurseFirstname: '',
  nurseLastname: '',
  nurseTitle: '',
  optionFacilityId: '',
  programKey: '',
  psychiatristConfirmEmail: '',
  psychiatristEmail: '',
  psychiatristFirstname: '',
  psychiatristLastname: '',
  psychiatristTitle: '',
};

const normalizeValue = (value: unknown) => `${value || ''}`.trim();

const formatDate = (value: string | null | undefined) => {
  const normalizedValue = normalizeValue(value);
  const match = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    return normalizedValue || '—';
  }

  return `${match[2]}/${match[3]}/${match[1]}`;
};

const getParticipantName = (participant: DiversionParticipantSearchItem) => {
  const firstAndLastName = `${normalizeValue(participant.firstname)} ${normalizeValue(participant.lastname)}`.trim();
  return firstAndLastName || normalizeValue(participant.fullname) || 'Unnamed participant';
};

const isBlockedPersonalDomain = (email: string, blockedDomains: string[]) => {
  const normalizedEmail = normalizeValue(email).toLowerCase();
  const emailDomain = normalizedEmail.split('@')[1] || '';
  return blockedDomains.map((domain) => normalizeValue(domain).toLowerCase()).includes(emailDomain);
};

const FieldErrorMessage = ({ message }: { message?: string }) => {
  if (!message) {
    return null;
  }

  return <p className="medcall2-intake__error">{message}</p>;
};

const IntakeLabel = ({ children, htmlFor, required = false }: {
  children: React.ReactNode;
  htmlFor: string;
  required?: boolean;
}) => (
  <Label className="medcall2-intake__label" htmlFor={htmlFor}>
    {required ? <span aria-hidden="true" className="medcall2-intake__required">*</span> : null}
    {children}<span aria-hidden="true" className="medcall2-intake__colon">:</span>
  </Label>
);

const InlineMessage = ({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: 'error' | 'warning';
}) => {
  const toneClassName = tone === 'error'
    ? 'border-[var(--semantic-error-border-soft)] bg-[var(--semantic-error-soft)] text-[var(--semantic-error)]'
    : 'border-[var(--semantic-warning-border-soft)] bg-[var(--semantic-warning-soft)] text-[var(--semantic-warning)]';

  return (
    <div className={cn('rounded-[var(--border-radius-medium)] border px-3 py-2 text-sm', toneClassName)} role="alert">
      <div className="flex items-start gap-2">
        <AlertCircle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="min-w-0 flex-1">{children}</div>
      </div>
    </div>
  );
};

export const Medcall2ParticipantIntakeDialog = ({
  onCreated,
  onOpenChange,
  open,
}: Medcall2ParticipantIntakeDialogProps) => {
  const apolloClient = useApolloClient();
  const navigate = useNavigate();
  const latestSearchRequest = useRef(createMedcallLatestRequestGuard()).current;
  const latestDuplicateRequest = useRef(createMedcallLatestRequestGuard()).current;
  const [searchDraft, setSearchDraft] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchActiveIndex, setSearchActiveIndex] = useState(0);
  const [searchResults, setSearchResults] = useState<DiversionParticipantSearchItem[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [hasCompletedSearch, setHasCompletedSearch] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState<DiversionParticipantSearchItem | null>(null);
  const [duplicateParticipant, setDuplicateParticipant] = useState<DuplicateParticipant | null>(null);
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [duplicateCheckConfirmed, setDuplicateCheckConfirmed] = useState(false);
  const [participantValidationError, setParticipantValidationError] = useState('');
  const [submitError, setSubmitError] = useState('');

  const {
    clearErrors,
    control,
    formState: { errors, isSubmitting },
    getValues,
    handleSubmit,
    register,
    reset,
    watch,
  } = useForm<IntakeFormValues>({
    defaultValues: DEFAULT_FORM_VALUES,
    shouldUnregister: true,
  });

  const { data, error: optionsError, loading: optionsLoading } = useQuery<Medcall2ParticipantIntakeOptionsData>(
    MEDCALL2_PARTICIPANT_INTAKE_OPTIONS,
    {
      fetchPolicy: 'network-only',
      skip: !open,
    },
  );
  const [createParticipant, { loading: createLoading }] = useMutation<Medcall2CreateParticipantData>(
    MEDCALL2_CREATE_PARTICIPANT,
  );

  const programKey = watch('programKey');
  const { programOptions, facilityOptions, filterDomains } = readMedcall2IntakeOptions(data?.medcallOptions);
  const selectedProgram = programOptions.find((option) => option.value === programKey) || null;
  const requiresClinicalContacts = selectedProgram?.requiresClinicalContacts === true;
  const requiresFacility = selectedProgram?.requiresFacility === true;
  const selectedParticipantIsIncomplete = Boolean(
    selectedParticipant
    && (
      !normalizeValue(selectedParticipant.firstname)
      || !normalizeValue(selectedParticipant.lastname)
      || !normalizeValue(selectedParticipant.dob)
    ),
  );

  const resetSearchState = React.useCallback(() => {
    latestSearchRequest.invalidate();
    latestDuplicateRequest.invalidate();
    setSearchDraft('');
    setSearchOpen(false);
    setSearchActiveIndex(0);
    setSearchResults([]);
    setSearchLoading(false);
    setSearchError('');
    setHasCompletedSearch(false);
    setSelectedParticipant(null);
    setDuplicateParticipant(null);
    setDuplicateLoading(false);
    setDuplicateCheckConfirmed(false);
    setParticipantValidationError('');
    setSubmitError('');
  }, [latestDuplicateRequest, latestSearchRequest]);

  const resetDialogState = React.useCallback(() => {
    reset(DEFAULT_FORM_VALUES);
    resetSearchState();
  }, [reset, resetSearchState]);

  useEffect(() => {
    if (open) {
      return;
    }

    resetDialogState();
  }, [open, resetDialogState]);

  const handleDialogOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && isSubmitting) return;
    if (!nextOpen) {
      resetDialogState();
    }

    onOpenChange(nextOpen);
  };

  const handleProgramChange = (value: string) => {
    reset({
      ...DEFAULT_FORM_VALUES,
      programKey: value,
    });
    clearErrors();
    resetSearchState();
  };

  const runParticipantSearch = async (searchText: string, requestId: number) => {
    try {
      const result = await apolloClient.query<Medcall2SearchDiversionParticipantsData>({
        fetchPolicy: 'network-only',
        query: MEDCALL_SEARCH_DIVERSION_PARTICIPANTS,
        variables: buildMedcallIntakeSearchVariables({
          programKey,
          routeBase: MEDCALL2_ROUTE_BASE,
          searchText,
        }),
      });

      if (!latestSearchRequest.isLatest(requestId)) {
        return;
      }

      const participants = result.data?.medcallSearchDiversionParticipants;
      if (result.errors?.length || !Array.isArray(participants)) throw new Error('Participant search was not confirmed');
      setSearchResults(participants);
      setHasCompletedSearch(true);
    } catch {
      if (!latestSearchRequest.isLatest(requestId)) {
        return;
      }

      setSearchResults([]);
      setSearchError('Unable to search participants. Please try again.');
      setHasCompletedSearch(true);
    }

    if (!latestSearchRequest.isLatest(requestId)) {
      return;
    }

    setSearchLoading(false);
  };

  const handleSearchDraftChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextSearchDraft = event.target.value;
    const normalizedSearchDraft = normalizeValue(nextSearchDraft);
    const requestId = latestSearchRequest.begin();
    latestDuplicateRequest.invalidate();
    setSearchDraft(nextSearchDraft);
    setSearchOpen(true);
    setSearchActiveIndex(0);
    setSearchResults([]);
    setSelectedParticipant(null);
    setDuplicateParticipant(null);
    setDuplicateLoading(false);
    setParticipantValidationError('');
    setSubmitError('');
    setSearchError('');
    setHasCompletedSearch(false);

    if (normalizedSearchDraft.length < 3) {
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    void runParticipantSearch(normalizedSearchDraft, requestId);
  };

  const handleParticipantSelect = async (participant: DiversionParticipantSearchItem) => {
    setDuplicateCheckConfirmed(false);
    setSearchOpen(false);
    latestSearchRequest.invalidate();
    const duplicateRequestId = latestDuplicateRequest.begin();
    setSearchDraft(`${getParticipantName(participant)} - ${formatDate(participant.dob)}`);
    setSearchResults([]);
    setSelectedParticipant(participant);
    setDuplicateParticipant(null);
    setParticipantValidationError('');
    setSubmitError('');
    setHasCompletedSearch(false);

    if (
      !normalizeValue(participant.firstname)
      || !normalizeValue(participant.lastname)
      || !normalizeValue(participant.dob)
    ) {
      setDuplicateLoading(false);
      return;
    }

    setDuplicateLoading(true);

    try {
      const result = await apolloClient.query<Medcall2FindDuplicateParticipantsData>({
        fetchPolicy: 'network-only',
        query: MEDCALL2_FIND_DUPLICATE_PARTICIPANTS,
        variables: {
          dob: participant.dob || null,
          firstname: participant.firstname || null,
          lastname: participant.lastname || null,
          odrparticipant: participant.odrparticipant || null,
          participantId: participant.participant,
        },
      });

      if (!latestDuplicateRequest.isLatest(duplicateRequestId)) {
        return;
      }

      const duplicates = result.data?.medcallFindDuplicateParticipants;
      if (result.errors?.length || !Array.isArray(duplicates)) throw new Error('Duplicate check was not confirmed');
      setDuplicateParticipant(duplicates[0] || null);
      setDuplicateCheckConfirmed(true);
    } catch {
      if (!latestDuplicateRequest.isLatest(duplicateRequestId)) {
        return;
      }

      setParticipantValidationError('Unable to check for an existing client. Select the participant again to retry.');
    }

    if (!latestDuplicateRequest.isLatest(duplicateRequestId)) {
      return;
    }

    setDuplicateLoading(false);
  };

  const validateParticipantSelection = () => {
    if (selectedParticipant) {
      return '';
    }

    if (!normalizeValue(searchDraft)) {
      return 'Search text is required';
    }

    if (normalizeValue(searchDraft).length < 3) {
      return 'Search text must be at least 3 characters';
    }

    return 'Please select a participant from the search results.';
  };

  const handleInvalidSubmit = () => {
    if (!getValues('programKey')) {
      return;
    }

    setParticipantValidationError(validateParticipantSelection());
  };

  const submitParticipant = async (values: IntakeFormValues) => {
    const selectionError = validateParticipantSelection();
    if (selectionError) {
      setParticipantValidationError(selectionError);
      return;
    }

    if (!selectedParticipant || selectedParticipantIsIncomplete || duplicateParticipant) {
      return;
    }
    if (duplicateLoading || !duplicateCheckConfirmed) return;
    if (optionsLoading || optionsError) return;

    setParticipantValidationError('');
    setSubmitError('');

    try {
      await loadMedcall2DiversionParticipantAliases({
        apolloClient,
        diversionParticipantId: selectedParticipant.participant,
        programKey: values.programKey,
        routeBase: MEDCALL2_ROUTE_BASE,
      });

      const result = await createParticipant({
        variables: {
          diversionParticipantId: selectedParticipant.participant,
          nurseEmail: requiresClinicalContacts ? normalizeValue(values.nurseEmail).toLowerCase() : null,
          nurseFirstname: requiresClinicalContacts ? normalizeValue(values.nurseFirstname) : null,
          nurseLastname: requiresClinicalContacts ? normalizeValue(values.nurseLastname) : null,
          nurseTitle: requiresClinicalContacts ? normalizeValue(values.nurseTitle) : null,
          optionFacilityId: requiresFacility ? Number(values.optionFacilityId) : null,
          preserveLegacyDuplicateSemantics: true,
          programKey: values.programKey,
          psychiatristEmail: requiresClinicalContacts && normalizeValue(values.psychiatristEmail)
            ? normalizeValue(values.psychiatristEmail).toLowerCase()
            : null,
          psychiatristFirstname: requiresClinicalContacts
            ? normalizeValue(values.psychiatristFirstname) || null
            : null,
          psychiatristLastname: requiresClinicalContacts
            ? normalizeValue(values.psychiatristLastname) || null
            : null,
          psychiatristTitle: requiresClinicalContacts ? normalizeValue(values.psychiatristTitle) || null : null,
        },
      });

      const response = result.data?.medcallCreateParticipant;
      if (result.errors?.length) {
        setSubmitError('Unable to add client. Please try again.');
        return;
      }
      if (response?.responseCode === 409 && response.duplicateParticipant) {
        setDuplicateParticipant(response.duplicateParticipant);
        setSubmitError(response.message || 'This client already exists in MedCall.');
        return;
      }

      if (response?.responseCode !== 200) {
        setSubmitError('Unable to add client. Please try again.');
        return;
      }

      const participantId = response.participant?.id;
      if (typeof participantId !== 'number' || !Number.isInteger(participantId) || participantId <= 0 || response.responseID !== participantId) {
        setSubmitError('Unable to add client. Please try again.');
        return;
      }

      if (onCreated) {
        try {
          await onCreated(participantId);
        } catch {
          toast.error('The client was added, but the list could not be refreshed. Refresh the page.');
        }
      }

      toast.success(response.message || 'Client added');
      resetDialogState();
      onOpenChange(false);
      navigate(`${MEDCALL2_ROUTE_BASE}/participants/${participantId}`, {
        state: {
          from: `${MEDCALL2_ROUTE_BASE}/all-clients`,
        },
      });
    } catch {
      setSubmitError('Unable to add client. Please try again.');
    }
  };

  const validateEmail = (email: string) => {
    const normalizedEmail = normalizeValue(email);
    if (!normalizedEmail) {
      return true;
    }

    if (!EMAIL_ADDRESS_REGEX.test(normalizedEmail)) {
      return 'Please use validated email address!';
    }

    if (isBlockedPersonalDomain(normalizedEmail, filterDomains)) {
      return 'Please do not use personal email addresses. It must be an email address from your agency!';
    }

    return true;
  };

  const renderSearchContent = () => {
    if (searchLoading) {
      return <div className="px-3 py-2 text-sm text-[var(--neutral-gray-80)]">Searching…</div>;
    }

    if (searchError) {
      return <div className="px-3 py-2 text-sm text-[var(--semantic-error)]">{searchError}</div>;
    }

    if (normalizeValue(searchDraft).length < 3) {
      return <div className="px-3 py-2 text-sm text-[var(--neutral-gray-80)]">Please enter 3 or more characters</div>;
    }

    if (hasCompletedSearch && searchResults.length < 1) {
      return <div className="px-3 py-2 text-sm text-[var(--neutral-gray-80)]">No Participant was found</div>;
    }

    if (searchResults.length < 1) {
      return null;
    }

    return searchResults.map((participant, index) => {
      const participantName = getParticipantName(participant);
      const participantResultId = `medcall2-intake-participant-${participant.participant}-${participant.odrparticipant || 0}-${index}`;
      const resultButton = (
        <button
          aria-selected={searchActiveIndex === index}
          className="medcall2-intake__search-option"
          id={participantResultId}
          key={participantResultId}
          onClick={() => void handleParticipantSelect(participant)}
          onMouseDown={(event) => event.preventDefault()}
          onMouseEnter={() => setSearchActiveIndex(index)}
          role="option"
          tabIndex={-1}
          type="button"
        >
          <span>{participantName}{participant.in_use ? ' (Already Added)' : ''}</span>
          <em className="medcall2-intake__search-dob">({formatDate(participant.dob)})</em>
        </button>
      );

      if (!participant.in_use) {
        return resultButton;
      }

      const tooltipLabel = `Already Added By ${normalizeValue(participant.username)}`;
      return (
        <Tooltip key={participantResultId}>
          <TooltipTrigger asChild title={tooltipLabel}>
            {resultButton}
          </TooltipTrigger>
          <TooltipContent className="medcall-legacy-tooltip">{tooltipLabel}</TooltipContent>
        </Tooltip>
      );
    });
  };

  const missingParticipantFields = [
    !normalizeValue(selectedParticipant?.dob) ? 'DOB' : '',
    !normalizeValue(selectedParticipant?.firstname) ? 'firstname' : '',
    !normalizeValue(selectedParticipant?.lastname) ? 'lastname' : '',
  ].filter(Boolean);

  return (
    <Dialog onOpenChange={handleDialogOpenChange} open={open}>
      <DialogContent
        className="medcall2-intake-dialog p-0"
        contentHeight="auto"
        contentMaxHeight="calc(100vh - 24px)"
        contentMaxWidth="calc(100vw - 16px)"
        contentTop="50%"
        contentWidth="520px"
        hideCloseButton
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          if (event.target instanceof HTMLElement) event.target.focus();
        }}
        onEscapeKeyDown={(event) => event.preventDefault()}
        overlayClassName="medcall2-intake-dialog__overlay"
      >
        <form
          className="medcall2-intake"
          noValidate
          onSubmit={handleSubmit(submitParticipant, handleInvalidSubmit)}
        >
          <DialogHeader className="medcall2-intake__header">
            <DialogTitle>Add client to Medcall</DialogTitle>
            <DialogDescription className="sr-only">
              Search for a diversion participant and add the participant to MedCall.
            </DialogDescription>
          </DialogHeader>

          <div className="medcall2-intake__body">
            <div className="medcall2-intake__field">
              <IntakeLabel htmlFor="medcall2-intake-program" required>Program</IntakeLabel>
              <div className="space-y-1">
                <Controller
                  control={control}
                  name="programKey"
                  render={({ field }) => (
                    <Select onValueChange={handleProgramChange} value={field.value}>
                      <SelectTrigger id="medcall2-intake-program">
                        <SelectValue placeholder="Select Program" />
                      </SelectTrigger>
                      <SelectContent className="medcall2-legacy-select-content">
                        {programOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  rules={{ required: 'Please choose the program from the list.' }}
                />
                <FieldErrorMessage message={errors.programKey?.message} />
              </div>
            </div>

            {programKey ? (
              <>
                {requiresFacility ? (
                  <div className="medcall2-intake__field">
                    <IntakeLabel htmlFor="medcall2-intake-facility" required>Facility</IntakeLabel>
                    <div className="space-y-1">
                      <Controller
                        control={control}
                        name="optionFacilityId"
                        render={({ field }) => (
                          <Select onValueChange={field.onChange} value={field.value}>
                            <SelectTrigger id="medcall2-intake-facility">
                              <SelectValue placeholder="Select Facility" />
                            </SelectTrigger>
                            <SelectContent className="medcall2-legacy-select-content">
                              {facilityOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        rules={{ required: 'Facility is required!' }}
                      />
                      <FieldErrorMessage message={errors.optionFacilityId?.message} />
                    </div>
                  </div>
                ) : null}

                <div className="medcall2-intake__search">
                  <Label className="sr-only" htmlFor="medcall2-intake-participant-search">Participant</Label>
                  <Popover.Root open={searchOpen} onOpenChange={setSearchOpen}>
                  <Popover.Anchor asChild>
                  <Input
                    aria-autocomplete="list"
                    aria-controls={searchOpen ? 'medcall2-intake-participant-results' : undefined}
                    aria-expanded={searchOpen}
                    aria-activedescendant={searchOpen && searchResults[searchActiveIndex]
                      ? `medcall2-intake-participant-${searchResults[searchActiveIndex].participant}-${searchResults[searchActiveIndex].odrparticipant || 0}-${searchActiveIndex}`
                      : undefined}
                    autoComplete="off"
                    id="medcall2-intake-participant-search"
                    onChange={handleSearchDraftChange}
                    onFocus={() => setSearchOpen(true)}
                    onKeyDown={(event) => {
                      if (event.key === 'Escape') {
                        event.preventDefault();
                        event.stopPropagation();
                        setSearchOpen(false);
                        return;
                      }
                      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                        event.preventDefault();
                        setSearchOpen(true);
                        if (searchResults.length > 0) {
                          const direction = event.key === 'ArrowDown' ? 1 : -1;
                          setSearchActiveIndex((index) => (index + direction + searchResults.length) % searchResults.length);
                        }
                        return;
                      }
                      if (event.key === 'Enter' && searchOpen && searchResults[searchActiveIndex]) {
                        event.preventDefault();
                        void handleParticipantSelect(searchResults[searchActiveIndex]);
                      }
                    }}
                    placeholder="Search with First Name, Last Name, Booking Number, CID..."
                    role="combobox"
                    value={searchDraft}
                  />
                  </Popover.Anchor>
                  <Popover.Portal>
                    <Popover.Content
                      align="start"
                      sideOffset={4}
                      className="medcall2-legacy-select-content medcall2-intake__search-popup"
                      id="medcall2-intake-participant-results"
                      onOpenAutoFocus={(event) => event.preventDefault()}
                      onCloseAutoFocus={(event) => event.preventDefault()}
                      onFocusOutside={(event) => {
                        if ((event.target as HTMLElement)?.id === 'medcall2-intake-participant-search') event.preventDefault();
                      }}
                      role="listbox"
                    >
                      <TooltipProvider>{renderSearchContent()}</TooltipProvider>
                    </Popover.Content>
                  </Popover.Portal>
                  </Popover.Root>
                  <FieldErrorMessage message={participantValidationError} />
                  {duplicateLoading ? (
                    <p className="text-sm text-[var(--neutral-gray-80)]">Checking for an existing MedCall client…</p>
                  ) : null}
                </div>

                {duplicateParticipant ? (
                  <InlineMessage tone="warning">
                    <div className="space-y-2">
                      <p>
                        A client with the name{' '}
                        <strong>{normalizeValue(duplicateParticipant.firstname)} {normalizeValue(duplicateParticipant.lastname)}</strong>{' '}
                        and the date of birth <strong>{formatDate(duplicateParticipant.dob)}</strong>{' '}
                        already exists in the MedCall database.
                      </p>
                      <Button
                        className="h-auto p-0 text-sm"
                        id="medcall2-intake-view-participant"
                        onClick={() => {
                          handleDialogOpenChange(false);
                          navigate(`${MEDCALL2_ROUTE_BASE}/participants/${duplicateParticipant.id}`, {
                            state: { from: `${MEDCALL2_ROUTE_BASE}/all-clients` },
                          });
                        }}
                        type="button"
                        variant="link"
                      >
                        <Eye aria-hidden="true" className="h-4 w-4" />
                        View Participant
                      </Button>
                    </div>
                  </InlineMessage>
                ) : null}

                {selectedParticipantIsIncomplete ? (
                  <InlineMessage tone="warning">
                    The participant with the name{' '}
                    <strong>
                      {normalizeValue(selectedParticipant?.firstname) || '[MISSING]'}{' '}
                      {normalizeValue(selectedParticipant?.lastname) || '[MISSING]'}
                    </strong>{' '}
                    of program <strong>{programKey.toUpperCase()}</strong> is missing:{' '}
                    {missingParticipantFields.map((field, index) => (
                      <React.Fragment key={field}>
                        {index > 0 ? ' and ' : null}<strong>{field}</strong>
                      </React.Fragment>
                    ))}
                    . Please complete participant data to be able to add as MedCall client.
                  </InlineMessage>
                ) : null}

                <div className="medcall2-intake__divider" />

                {requiresClinicalContacts ? (
                  <>
                    <section aria-labelledby="medcall2-intake-nurse-heading">
                      <h3 className="text-sm font-semibold underline" id="medcall2-intake-nurse-heading">Nurse Detail</h3>
                      <div className="medcall2-intake__field">
                        <IntakeLabel htmlFor="medcall2-intake-nurse-firstname" required>Firstname</IntakeLabel>
                        <div className="space-y-1">
                          <Input
                            id="medcall2-intake-nurse-firstname"
                            {...register('nurseFirstname', { required: 'Firstname is required!' })}
                          />
                          <FieldErrorMessage message={errors.nurseFirstname?.message} />
                        </div>
                      </div>
                      <div className="medcall2-intake__field">
                        <IntakeLabel htmlFor="medcall2-intake-nurse-lastname" required>Lastname</IntakeLabel>
                        <div className="space-y-1">
                          <Input
                            id="medcall2-intake-nurse-lastname"
                            {...register('nurseLastname', { required: 'Lastname is required!' })}
                          />
                          <FieldErrorMessage message={errors.nurseLastname?.message} />
                        </div>
                      </div>
                      <div className="medcall2-intake__field">
                        <IntakeLabel htmlFor="medcall2-intake-nurse-title" required>Title</IntakeLabel>
                        <div className="space-y-1">
                          <Controller
                            control={control}
                            name="nurseTitle"
                            render={({ field }) => (
                              <Select onValueChange={field.onChange} value={field.value}>
                                <SelectTrigger id="medcall2-intake-nurse-title">
                                  <SelectValue placeholder="Select Title" />
                                </SelectTrigger>
                                <SelectContent className="medcall2-legacy-select-content">
                                  {NURSE_TITLES.map((title) => (
                                    <SelectItem key={title} value={title}>{title}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                            rules={{ required: 'Title is required!' }}
                          />
                          <FieldErrorMessage message={errors.nurseTitle?.message} />
                        </div>
                      </div>
                      <div className="medcall2-intake__field">
                        <IntakeLabel htmlFor="medcall2-intake-nurse-email" required>Email</IntakeLabel>
                        <div className="space-y-1">
                          <Input
                            id="medcall2-intake-nurse-email"
                            type="email"
                            {...register('nurseEmail', {
                              required: 'Email is required!',
                              validate: validateEmail,
                            })}
                          />
                          <FieldErrorMessage message={errors.nurseEmail?.message} />
                        </div>
                      </div>
                      <div className="medcall2-intake__field">
                        <IntakeLabel htmlFor="medcall2-intake-nurse-confirm-email" required>Retype Email</IntakeLabel>
                        <div className="space-y-1">
                          <Input
                            id="medcall2-intake-nurse-confirm-email"
                            type="email"
                            {...register('nurseConfirmEmail', {
                              required: 'Please retype your email address!',
                              validate: (value) => (
                                value === getValues('nurseEmail')
                                || 'The two emails that you entered do not match!'
                              ),
                            })}
                          />
                          <FieldErrorMessage message={errors.nurseConfirmEmail?.message} />
                        </div>
                      </div>
                    </section>

                  </>
                ) : null}
                <div className="medcall2-intake__divider" />
                {requiresClinicalContacts ? (
                    <section aria-labelledby="medcall2-intake-psychiatrist-heading">
                      <h3 className="text-sm font-semibold underline" id="medcall2-intake-psychiatrist-heading">Psychiatrist</h3>
                      <div className="medcall2-intake__field">
                        <IntakeLabel htmlFor="medcall2-intake-psychiatrist-firstname">Firstname</IntakeLabel>
                        <div><Input id="medcall2-intake-psychiatrist-firstname" {...register('psychiatristFirstname')} /></div>
                      </div>
                      <div className="medcall2-intake__field">
                        <IntakeLabel htmlFor="medcall2-intake-psychiatrist-lastname">Lastname</IntakeLabel>
                        <div><Input id="medcall2-intake-psychiatrist-lastname" {...register('psychiatristLastname')} /></div>
                      </div>
                      <div className="medcall2-intake__field">
                        <IntakeLabel htmlFor="medcall2-intake-psychiatrist-title">Title</IntakeLabel>
                        <Controller
                          control={control}
                          name="psychiatristTitle"
                          render={({ field }) => (
                            <Select onValueChange={field.onChange} value={field.value}>
                              <SelectTrigger id="medcall2-intake-psychiatrist-title">
                                <SelectValue placeholder="Select Title" />
                              </SelectTrigger>
                              <SelectContent className="medcall2-legacy-select-content">
                                {PSYCHIATRIST_TITLES.map((title) => (
                                  <SelectItem key={title} value={title}>{title}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                      </div>
                      <div className="medcall2-intake__field">
                        <IntakeLabel htmlFor="medcall2-intake-psychiatrist-email">Email</IntakeLabel>
                        <div className="space-y-1">
                          <Input
                            id="medcall2-intake-psychiatrist-email"
                            type="email"
                            {...register('psychiatristEmail', { validate: validateEmail })}
                          />
                          <FieldErrorMessage message={errors.psychiatristEmail?.message} />
                        </div>
                      </div>
                      <div className="medcall2-intake__field">
                        <IntakeLabel htmlFor="medcall2-intake-psychiatrist-confirm-email">Retype Email</IntakeLabel>
                        <div className="space-y-1">
                          <Input
                            id="medcall2-intake-psychiatrist-confirm-email"
                            type="email"
                            {...register('psychiatristConfirmEmail', {
                              validate: (value) => (
                                !value
                                || value === getValues('psychiatristEmail')
                                || 'The two emails that you entered do not match!'
                              ),
                            })}
                          />
                          <FieldErrorMessage message={errors.psychiatristConfirmEmail?.message} />
                        </div>
                      </div>
                    </section>
                ) : null}
              </>
            ) : null}

            {optionsError ? <InlineMessage tone="error">Unable to load intake options. Close this dialog and try again.</InlineMessage> : null}
            {submitError ? (
              <InlineMessage tone="error">
                An error saving this client:<br /><strong>{submitError}</strong>
              </InlineMessage>
            ) : null}
          </div>

          <DialogFooter className="medcall2-intake__footer">
            <Button id="medcall2-intake-cancel" disabled={isSubmitting} onClick={() => handleDialogOpenChange(false)} type="button" variant="outline">
              Cancel
            </Button>
            <Button
              disabled={
                isSubmitting
                || createLoading
                || optionsLoading
                || Boolean(optionsError)
                || duplicateLoading
                || (Boolean(selectedParticipant) && !duplicateCheckConfirmed)
                || selectedParticipantIsIncomplete
                || Boolean(duplicateParticipant)
              }
              id="medcall2-intake-submit"
              type="submit"
            >
              Submit
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
